import { createMockableHook } from '@repo/common/utils/createMockableHook';
import type {
	QueryFunctionContext,
	QueryKey,
	UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { apiClient, getEndpoints } from '../../common';
import { baseQueryHooksFactory } from '../../factories';
import queryClient from '../../queryClient';
import type { IReport } from '../../types';
import { useBaseModel } from '../base';
import { reportsQueryKeyFactory } from './constants';

export interface IUseReportArgs {
	type?: 'homepage' | 'analytics' | 'teams';
	teamId?: string;
	reportId?: string;
	options?: Omit<
		UseQueryOptions<IReport, Error, IReport>,
		'queryKey' | 'queryFn' | 'persister'
	>;
}

function useReportInternal({
	type = 'homepage',
	teamId,
	reportId,
	options = {},
}: IUseReportArgs) {
	let queryKey: QueryKey = reportsQueryKeyFactory.byId(type);
	let { namespace } = reportsQueryKeyFactory;
	let url = getEndpoints(namespace).byId(type);

	if (teamId !== undefined) {
		queryKey = reportsQueryKeyFactory.byArgs(type, teamId);
		namespace = reportsQueryKeyFactory.namespace;
		url = getEndpoints(namespace).byPath([teamId, type]);
	} else if (type === 'analytics' && reportId !== undefined) {
		queryKey = reportsQueryKeyFactory.byArgs(type, reportId);
		namespace = reportsQueryKeyFactory.namespace;
		url = getEndpoints(namespace).byPath([type, reportId]);
	}

	const queryFn = useCallback(
		async ({ signal }: QueryFunctionContext<QueryKey>) => {
			const { data } = await apiClient.get<IReport>(url, { signal });
			return data;
		},
		[url]
	);

	return useBaseModel<IReport>({
		id: type,
		queryKey,
		namespace,
		queryFn,
		options: {
			gcTime: 1000 * 60 * 60, // 1 hour
			staleTime: 1000 * 60 * 60, // 1 hour,
			...options,
		},
	});
}

const [useReport, MockUseReport] = createMockableHook(useReportInternal);

const {
	useCreateReport,
	useDeleteReport,
	useUpdateReport,
	updateReport,
	fetchReportList,
	useReportList,
} = baseQueryHooksFactory<IReport, 'Report'>('Report', reportsQueryKeyFactory);

/**
 * Prefetches the homepage report and stores it in the query cache.
 * This significantly reduces the time it takes to load the homepage,
 * since the data is already in the cache.
 */
async function prefetchHomepageReport(): Promise<void> {
	const queryKey = reportsQueryKeyFactory.byId('homepage');
	const { namespace } = reportsQueryKeyFactory;
	const url = getEndpoints(namespace).byId('homepage');

	await queryClient.prefetchQuery({
		queryKey,
		queryFn: async ({ signal }: QueryFunctionContext<QueryKey>) => {
			const { data } = await apiClient.get<IReport>(url, { signal });
			return data;
		},
	});
}

export {
	fetchReportList,
	MockUseReport,
	prefetchHomepageReport,
	updateReport,
	useCreateReport,
	useDeleteReport,
	useReport,
	useReportList,
	useUpdateReport,
};
